import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CustomDataGrid from '../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  debounce,
  PaginationModel,
  useAbort,
  usePagination
} from '../../../../../utils/commonFunctions';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import { useForm } from 'react-hook-form';
import GoogleMap from '../../../../../common/components/maps/googlemap/GoogleMap';
import { Icon } from '@iconify/react';
import GoogleMarkers from '../../../../../common/components/maps/googlemarkers/GoogleMarkers';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import {
  map,
  updateCenter,
  updateZoom
} from '../../../../../common/redux/reducer/commonSlices/mapSlice';
import {
  getPickupLocationAction,
  pickupConfigureUpdateAction
} from '../../../redux/reducer/autoPlannerSlices/pickupConfigurationSlice';
import PlaceIcon from '@mui/icons-material/Place';
import './PickupLocations.scss';

interface Pickup {
  id: number;
  latitude: number;
  longitude: number;
  locationAddress: string;
  isMain: 0 | 1;
}

const PickupLocations = () => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [pickupRows, setPickupRows] = useState<Pickup[]>([]);
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { pageSize, pageNo, pagination, setPageNo, setPageSize } = usePagination(10);

  const dispatch = useAppDispatch();
  const { control } = useForm();

  const { data: pickupLocation, isLoading: pickupLocationIsLoading } = useAppSelector(
    (state: any) => state.pickupLocation
  );

  const updatePickupConfigure = useAppSelector(
    (state: any) => state.updatePickupConfigure
  );

  const locationsListColumn = [
    {
      field: 'locationAddress',
      headerName: 'Address',
      minWidth: 850,
      flex: 1,
      sortable: false
    },
    {
      field: 'View',
      headerName: 'View Location',
      minWidth: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }: { row: Pickup }) => (
        <Box sx={{ width: '40%', display: 'flex', justifyContent: 'space-evenly' }}>
          <Tooltip title='View location' placement='right' arrow>
            <IconButton>
              <PlaceIcon
                onClick={event => {
                  handleViewLocation(row);
                }}
                className='location-icon-btn'
              />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const handleViewLocation = (row: Pickup) => {
    setIsOpen(true);
    const marker = [
      {
        id: 'Actual address',
        name: 'myplace',
        lat: row.latitude,
        lng: row.longitude,
        address: row.locationAddress,
        info: { Mylocation: row.locationAddress }
      }
    ];
    dispatch(map(marker));
    dispatch(updateZoom(marker));
    dispatch(updateCenter(marker));
  };

  // for seleted row
  const handleSelectionChange = async (selection: number[]) => {
    // setSelectedRows(selection);
    const difference =
      selection.filter(item => !selectedRows.includes(item))[0] ||
      selectedRows.filter(item => !selection.includes(item))[0];
    const lastSelectedObject = pickupLocation?.data?.pickupHotelViewList.find(
      (item: Pickup) => item.id === difference
    );

    if (lastSelectedObject) {
      const updatedObject = {
        ...lastSelectedObject,
        isMain: !lastSelectedObject.isMain ? 1 : 0
      };
      const getPayload = {
        pageNo: pageNo,
        pageSize: pageSize,
        location: filter
      };
      const payload = {
        updatedObject,
        getPayload
      };
      await dispatch(pickupConfigureUpdateAction(payload));
    }
  };

  const handlePaginationModelChange = (newPaginationModel: PaginationModel) => {
    dispatch(
      getPickupLocationAction({
        ...pagination(newPaginationModel),
        location: filter
      })
    );
  };

  const handleFilterChange = useCallback(
    debounce((event: string) => {
      setFilter(event);
      const payload = {
        pageNo: 1,
        pageSize: pageSize,
        location: event
      };
      setPageNo(1);
      dispatch(getPickupLocationAction(payload));
    }),
    [pageSize]
  );

  const handleCloseDetails = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (
      pickupLocation?.data !== null &&
      pickupLocation?.data?.pickupHotelViewList.length > 0
    ) {
      const pickupData = pickupLocation?.data?.pickupHotelViewList;
      setPickupRows(pickupData);
      setSelectedRows(
        pickupData
          .filter((item: Pickup) => item.isMain === 1)
          .map((item: Pickup) => item.id)
      );
    } else setPickupRows([]);
  }, [pickupLocation]);

  useEffect(() => {
    const payload = {
      pageNo: pageNo,
      pageSize: pageSize,
      location: filter
    };
    dispatch(getPickupLocationAction(payload));
  }, []);

  return (
    <Box className='location-container'>
      <Box className='location-search'>
        <Box className='pickup-location-heading'>
          <Typography className='header'>Main pickup locations</Typography>
          <Typography className='badge'>
            {pickupLocation?.data?.totalCount - pickupLocation?.data?.notMainCount || 0}
          </Typography>
        </Box>
        <Box className='pickup-search'>
          <Box sx={{ width: 200 }}>
            <CustomTextField
              name='search'
              control={control}
              id='filter-input'
              placeholder='Search Address'
              value={filter}
              variant='standard'
              icon={<SearchOutlinedIcon color='primary' />}
              onChangeCallback={(e: any) => handleFilterChange(e)}
            />
          </Box>
        </Box>
      </Box>
      <Box className='pickup-locations-table'>
        <CustomDataGrid
          rows={pickupRows}
          columns={locationsListColumn}
          checkboxSelection={true}
          onRowSelectionModelChange={handleSelectionChange}
          type='logistics'
          rowCount={pickupLocation?.data?.totalCount || 0}
          onPaginationModelChange={handlePaginationModelChange}
          pageNo={pageNo}
          pageSize={pageSize}
          paginationModel={{
            page: pageNo ? pageNo - 1 : 0,
            pageSize: pageSize ? pageSize : 10
          }}
          loading={
            updatePickupConfigure?.isLoading ||
            pickupLocation?.isLoading ||
            pickupLocationIsLoading
          }
          selection={selectedRows}
          disableSelectAll={true}
        />
      </Box>
      {isOpen && (
        <Dialog
          open={isOpen}
          fullScreen
          onClose={() => handleCloseDetails()}
          sx={{ margin: '20px' }}
          className='pickup-location-view'
        >
          <Box className='close-pickup-location' onClick={handleCloseDetails}>
            <CustomIconButton
              category='CloseValue'
              onClick={() => handleCloseDetails()}
            />
          </Box>
          <Box className='view-pickup-location'>
            <GoogleMap>
              <GoogleMarkers />
            </GoogleMap>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default PickupLocations;
